/**
 * core/glambox/GlamboxRecaptcha
 * ----------------------------------------------------------------------
 * Helper para carregamento e implementação do Google reCAPTCHA em projetos 
 * da Glambox.
 * 
 * @since     0.0.1
 */
export class GlamboxRecaptcha {
  /**
   * Elemento HTML que será usado para abrigar o captcha.
   * 
   * @type {HTMLElement}
   */
  static ELEMENT;

  /**
   * Se estamos executando em modo de desenvolvimento ou não.
   * 
   * @type {boolean}
   */
  static DEVELOPMENT;

  /**
   * Armazena um ID de um setInterval, utilizado como timer para carregamento 
   * e triggering do script do reCAPTCHA.
   * 
   * @type {number}
   */
  static INIT_INTERVAL;

  /**
   * Se o captcha foi, ou não, inicializado.
   * 
   * @type {boolean}
   */
  static INITIALIZED = false;

  /**
   * Chave do recaptcha, atualizado toda vez que o método `grecaptcha.execute()` 
   * é invocado.
   * 
   * @type {string}
   */
  static KEY = null;

  /**
   * Vetor que armazena funções observadoras, que recebem o código do captcha 
   * toda vez que for gerado.
   * 
   * @type {function[]}
   */
  static POOL = [];

  /**
   * ID do elemento aonde o captcha será renderizado.
   * 
   * @type {string}
   */
  static RECAPTCHA_ELEMENT_ID;

  /**
   * Site key do captcha a ser utilizado.
   * 
   * @type {string}
   */
  static RECAPTCHA_SITEKEY;

  /**
   * Tamanho do captcha a ser criado.
   * 
   * @type {string}
   */
  static RECAPTCHA_SIZE;

  /**
   * Inicializa e executa o carregamento do script do Google reCAPTCHA.
   * 
   * @param {string} siteKey 
   *     Site key exclusivo da aplicação 
   * @param {function|function[]} watcher 
   *     Uma função e/ou funções a serem executadas quando o captcha for 
   *     gerado, devem receber como parâmetro o código do captcha 
   * @param {string} captchaSize 
   *     Tamanho do captcha a ser criado, padrão 'invisible'
   * @param {string} captchaElementId 
   *     ID do elemento aonde o captcha será renderizado
   */
  static init (
    siteKey, 
    watcher = [], 
    captchaSize = "invisible", 
    captchaElementId = "grecaptcha-container"
  ) {
    if (Array.isArray(watcher)) {
      for (let w = 0; w < watcher.length; w++) {
        if (typeof watcher[w] === "function") this.POOL.push(watcher[w]);
      }
    } else if (typeof watcher === "function") {
      this.POOL.push(watcher);
    }

    GlamboxRecaptcha.DEVELOPMENT = (typeof process !== "undefined" && process.env.NODE_ENV !== "production");
    GlamboxRecaptcha.RECAPTCHA_ELEMENT_ID = captchaElementId;
    GlamboxRecaptcha.RECAPTCHA_SITEKEY = siteKey;
    GlamboxRecaptcha.RECAPTCHA_SIZE = captchaSize;
    GlamboxRecaptcha.loadCaptcha();
  }

  /**
   * Realiza um refresh do reCAPTCHA.
   */
  static reset () {
    if (GlamboxRecaptcha.INITIALIZED) {
      window.grecaptcha.reset(GlamboxRecaptcha.WIDGET_ID);
    }
  }

  /**
   * Solicita uma nova chave do reCAPTCHA.
   */
  static execute () {
    if (GlamboxRecaptcha.INITIALIZED) {
      window.grecaptcha.execute(GlamboxRecaptcha.WIDGET_ID);
    }
  }
  
  /**
   * Retorna a string do reCAPTCHA ou nulo, caso não tenha sido possível 
   * gerar.
   * 
   * @returns {string|null}
   */
  static get () {
    return (GlamboxRecaptcha.INITIALIZED && GlamboxRecaptcha.KEY) 
      ? GlamboxRecaptcha.KEY 
      : null;
  }

  /**
   * Adiciona uma função listener ao pool de observadores.
   * 
   * @param {function|function[]} func 
   *     Listener a ser adicionado ao pool de listeners, ou um array de 
   *     listeners 
   */
  static addListener (func) {
    if (Array.isArray(func)) {
      for (let f = 0; f < func.length; f++) {
        if (typeof func[f] === "function") {
          if (GlamboxRecaptcha.KEY) func[f](GlamboxRecaptcha.KEY);
          GlamboxRecaptcha.POOL.push(func[f]);
        }
      }
    } else if (typeof func === "function") {
      if (GlamboxRecaptcha.KEY) func(GlamboxRecaptcha.KEY);
      GlamboxRecaptcha.POOL.push(func);
    }
  }

  /**
   * Reseta o pool de listeners.
   */
  static clearListeners () {
    GlamboxRecaptcha.POOL = [];
  }

  static removeCaptcha () {
    GlamboxRecaptcha.clearListeners();

    const parent = GlamboxRecaptcha.ELEMENT.parentElement;
    parent.removeChild(GlamboxRecaptcha.ELEMENT);
  }

  // EVENTOS
  // --------------------------------------------------------------------

  /**
   * Carrega e realiza o embed do script do Google reCAPTCHA na página, dentro 
   * de uma tag script.
   */
  static loadCaptcha () {
    if (typeof window !== "undefined") {
      if (GlamboxRecaptcha.DEVELOPMENT) {
        console.log("> RECAPTCHA: Script Loaded");
      }

      if (GlamboxRecaptcha.INIT_INTERVAL) clearInterval(GlamboxRecaptcha.INIT_INTERVAL);

      if (typeof window.grecaptcha === "undefined") {
        // Define método a ser executado no load com hoisting
        window.captchaOnLoad = GlamboxRecaptcha.onCaptchaLoad;
        
        const $URL = "https://www.google.com/recaptcha/api.js",
              $_QS = "?onload=captchaOnLoad&render=explicit",
              $TAG = document.createElement("script");
        
        $TAG.type = "text/javascript";
        $TAG.src = $URL + $_QS;
        $TAG.async = true;
        $TAG.defer = true;
        
        document.body.appendChild($TAG);
      } else {
        GlamboxRecaptcha.onCaptchaLoad();
      }
    } else {
      GlamboxRecaptcha.INIT_INTERVAL = setInterval(
        () => {
          GlamboxRecaptcha.init(
            GlamboxRecaptcha.RECAPTCHA_SITEKEY,
            [],
            GlamboxRecaptcha.RECAPTCHA_SIZE,
            GlamboxRecaptcha.RECAPTCHA_ELEMENT_ID
          );
        },
        1000
      );
    }
  }

  /**
   * Executado quando o reCAPTCHA é carregado, responsável por renderizar 
   * o elemento do recaptcha.
   */
  static onCaptchaLoad () {
    GlamboxRecaptcha.ELEMENT = document.createElement("div");
    GlamboxRecaptcha.ELEMENT.id = GlamboxRecaptcha.RECAPTCHA_ELEMENT_ID;

    document.body.appendChild(
      GlamboxRecaptcha.ELEMENT
    );

    if (GlamboxRecaptcha.DEVELOPMENT) {
      console.log("> RECAPTCHA: Element Loaded");
    }

    GlamboxRecaptcha.WIDGET_ID = window.grecaptcha.render(
      GlamboxRecaptcha.RECAPTCHA_ELEMENT_ID, 
      {
        sitekey: GlamboxRecaptcha.RECAPTCHA_SITEKEY,
        size: GlamboxRecaptcha.RECAPTCHA_SIZE,
        callback: GlamboxRecaptcha.onRecaptchaExecute
      }
    );

    window.grecaptcha.ready(GlamboxRecaptcha.onCaptchaReady);
  }

  /**
   * Executado quando o captcha está pronto, chama `grecaptcha.execute()` e 
   * preenche o valor de `GlamboxRecaptcha.KEY`.
   * 
   * Isso também executa o trigger em todos os observadores dentro do 
   * array `GlamboxRecaptcha.POOL`, repassando o valor do captcha.
   * 
   * Define como `true` o valor de `GlamboxRecaptcha.INITIALIZED`.
   */
  static onCaptchaReady () {
    if (GlamboxRecaptcha.DEVELOPMENT) console.log("> RECAPTCHA: Ready");
    GlamboxRecaptcha.INITIALIZED = true;

    //window.grecaptcha.execute(GlamboxRecaptcha.WIDGET_ID);
  }

  /**
   * Callback executado toda vez que `grecaptcha.execute()` é chamado, define 
   * o valor atual do recaptcha.
   * 
   * Também repassa o valor para qualquer função observadora que esteja dentro 
   * do `POOL`.
   * 
   * @param {string} recaptcha 
   *     String retornada pela API do reCAPTCHA
   */
  static onRecaptchaExecute (recaptcha) {
    GlamboxRecaptcha.KEY = recaptcha;

    for (let w = 0; w < GlamboxRecaptcha.POOL.length; w++) {
      if (typeof GlamboxRecaptcha.POOL[w] === "function") {
        GlamboxRecaptcha.POOL[w](recaptcha);
      }
    }

    GlamboxRecaptcha.reset();
  }
}
